import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Loader2,
  MessageCircle,
} from "lucide-react";
import Navbar from "../../components/layout/Navbar";
import { Footer } from "../../components/layout/Footer";
import {
  clearJBACheckoutRecord,
  clearJBALead,
  fulfillJBAPayment,
  jbaConfig,
  loadJBACheckoutRecord,
  loadJBALead,
} from "../../utils/jba";

type PaymentState = "verifying" | "success" | "attention" | "cancelled";
const VERIFICATION_RETRY_LIMIT = 3;
const VERIFICATION_RETRY_DELAY_MS = 2000;
const VERIFYING_MESSAGE =
  "Confirming your payment. Please do not close or refresh this page.";
const SUCCESSFUL_PAYMENT_STATUSES = [
  "success",
  "successful",
  "approved",
  "completed",
];
const CANCELLED_PAYMENT_STATUSES = [
  "cancelled",
  "canceled",
  "abandoned",
  "failed",
];

const JBAPaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [paymentState, setPaymentState] = useState<PaymentState>("verifying");
  const [message, setMessage] = useState(VERIFYING_MESSAGE);
  const redirectUrl =
    "https://chat.whatsapp.com/LBNvpb1Sx5C2rotujKYvmZ?mode=gi_t";
  const [retryNonce, setRetryNonce] = useState(0);
  const hasOpenedRedirectRef = useRef(false);

  const rawStatus =
    searchParams.get("status") || searchParams.get("response") || "";
  const normalizedStatus = rawStatus.trim().toLowerCase();
  const txRef =
    searchParams.get("tx_ref") ||
    searchParams.get("txref") ||
    searchParams.get("trxref") ||
    searchParams.get("reference") ||
    "";
  const transactionId =
    searchParams.get("transaction_id") ||
    searchParams.get("transactionId") ||
    searchParams.get("trans") ||
    searchParams.get("id") ||
    "";

  const checkoutRecord = useMemo(
    () => (txRef ? loadJBACheckoutRecord(txRef) : null),
    [txRef]
  );
  const savedLead = useMemo(() => loadJBALead(), []);
  const fulfillmentLead = checkoutRecord?.lead || savedLead || null;

  useEffect(() => {
    if (paymentState !== "verifying") {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [paymentState]);

  useEffect(() => {
    let isCancelled = false;
    let retryTimer: number | null = null;

    const verifyAndFulfill = async () => {
      const statusLooksCancelled =
        CANCELLED_PAYMENT_STATUSES.includes(normalizedStatus);
      const statusLooksSuccessful =
        !normalizedStatus ||
        SUCCESSFUL_PAYMENT_STATUSES.includes(normalizedStatus);
      const canVerify = Boolean(txRef && statusLooksSuccessful);

      if (!canVerify) {
        setPaymentState("cancelled");
        setMessage(
          statusLooksCancelled
            ? "Payment was cancelled."
            : "Payment did not complete successfully."
        );
        return;
      }

      setPaymentState("verifying");
      setMessage(VERIFYING_MESSAGE);

      let lastError: unknown = null;

      for (
        let attempt = 1;
        attempt <= VERIFICATION_RETRY_LIMIT && !isCancelled;
        attempt += 1
      ) {
        try {
          const response = await fulfillJBAPayment({
            transaction_id: transactionId,
            tx_ref: txRef,
            product_code: jbaConfig.productCode,
            expected_amount: jbaConfig.amount,
            expected_currency: jbaConfig.currency,
            customer: fulfillmentLead,
          });

          clearJBALead();
          clearJBACheckoutRecord(txRef);

          setPaymentState("success");
          setMessage(
            response?.message ||
            (redirectUrl
              ? "Payment verified. Your PDFs are on the way."
              : "Payment verified. Your PDFs are being prepared.")
          );
          return;
        } catch (error) {
          console.error(error);
          lastError = error;

          if (isCancelled) return;

          if (attempt < VERIFICATION_RETRY_LIMIT) {
            setMessage(
              `Still confirming payment. Retrying ${attempt + 1} of ${VERIFICATION_RETRY_LIMIT}...`
            );

            await new Promise<void>((resolve) => {
              retryTimer = window.setTimeout(
                resolve,
                VERIFICATION_RETRY_DELAY_MS
              );
            });
          }
        }
      }

      if (isCancelled) return;

      setPaymentState("attention");
      setMessage(
        lastError instanceof Error
          ? `${lastError.message} Retry below.`
          : "Payment was received, but delivery needs attention. Retry below."
      );
    };

    verifyAndFulfill();

    return () => {
      isCancelled = true;
      if (retryTimer !== null) {
        window.clearTimeout(retryTimer);
      }
    };
  }, [
    checkoutRecord?.lead,
    fulfillmentLead,
    normalizedStatus,
    retryNonce,
    transactionId,
    txRef,
  ]);

  useEffect(() => {
    if (paymentState !== "success" || !redirectUrl) return;
    if (hasOpenedRedirectRef.current) return;

    hasOpenedRedirectRef.current = true;
    const timer = window.setTimeout(() => {
      const openedWindow = window.open(
        redirectUrl,
        "_blank",
        "noopener,noreferrer"
      );

      if (!openedWindow) {
        hasOpenedRedirectRef.current = false;
      }
    }, 1500);

    return () => window.clearTimeout(timer);
  }, [paymentState, redirectUrl]);

  const Icon =
    paymentState === "success"
      ? CheckCircle2
      : paymentState === "verifying"
        ? Loader2
        : AlertCircle;

  const iconClasses =
    paymentState === "success"
      ? "text-emerald-600"
      : paymentState === "verifying"
        ? "animate-spin text-blue-600"
        : "text-amber-600";

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <main className="mx-auto max-w-3xl px-4 py-12 md:py-16">
        <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm md:p-12">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-slate-50">
            <Icon className={`h-9 w-9 ${iconClasses}`} />
          </div>

          <p className="mt-6 text-sm font-semibold uppercase tracking-[0.25em] text-blue-600">
            JBA Payment Status
          </p>

          <h1 className="mt-3 text-3xl font-bold text-slate-900">
            {paymentState === "success"
              ? "Payment completed"
              : paymentState === "verifying"
                ? "Verifying your payment"
                : paymentState === "cancelled"
                  ? "Payment not completed"
                  : "Payment needs attention"}
          </h1>

          <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-slate-600 md:text-base">
            {message}
          </p>

          {paymentState === "verifying" && (
            <div className="mx-auto mt-6 max-w-2xl rounded-2xl border border-amber-200 bg-amber-50 p-4 text-left text-sm font-medium text-amber-800">
              Do not close or refresh this window while your payment is being
              processed.
            </div>
          )}

          {txRef && (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                Transaction Reference
              </p>
              <p className="mt-2 break-all font-mono text-sm text-slate-900">
                {txRef}
              </p>
            </div>
          )}

          {paymentState === "success" && redirectUrl && (
            <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-left">
              <div className="flex items-center gap-3 text-emerald-700">
                <MessageCircle className="h-5 w-5" />
                <p className="font-semibold">
                  Click continue now to join the WhatsApp group.
                </p>
              </div>
              <a
                href={redirectUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-emerald-800"
              >
                Continue now
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          )}

          {paymentState === "success" && !redirectUrl && (
            <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-left text-sm text-slate-700">
              The payment is confirmed, but no WhatsApp group link has been
              configured yet.
            </div>
          )}

          {paymentState !== "verifying" && (
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              {paymentState === "attention" && (
                <button
                  type="button"
                  onClick={() =>
                    setRetryNonce((currentValue) => currentValue + 1)
                  }
                  className="inline-flex items-center justify-center rounded-2xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700"
                >
                  Retry verification
                </button>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default JBAPaymentSuccess;
